import {ArrowPathIcon} from '@heroicons/react/20/solid';
import {ConnectionStatus, keepsPanelOpen} from './connectionStatus.ts';

interface StatusCopy {
    eyebrow: string;
    title: string;
    detail: string;
    action: string;
}

const DOT_CLASS: Record<ConnectionStatus, string> = {
    [ConnectionStatus.Connected]: 'bg-emerald-500',
    [ConnectionStatus.Expiring]: 'bg-amber-500',
    [ConnectionStatus.Expired]: 'bg-red-500',
    [ConnectionStatus.Manual]: 'bg-gray-400',
};


const ACTION_CLASS: Record<ConnectionStatus, string> = {
    [ConnectionStatus.Connected]: '',
    [ConnectionStatus.Expiring]: 'bg-amber-500 text-amber-950 hover:bg-amber-400',
    [ConnectionStatus.Expired]: 'bg-red-600 text-white hover:bg-red-500',
    [ConnectionStatus.Manual]: '',
};

function statusCopy(status: ConnectionStatus, lastSynced: string, connectionLabel: string): StatusCopy {
    switch (status) {
        case ConnectionStatus.Connected:
            return {
                eyebrow: connectionLabel,
                title: 'Verbonden',
                detail: lastSynced
                    ? `Laatst gesynchroniseerd ${lastSynced}.`
                    : 'Transacties worden automatisch gesynchroniseerd.',
                action: '',
            };
        case ConnectionStatus.Expiring:
            return {
                eyebrow: connectionLabel,
                title: 'Verloopt binnenkort',
                detail: 'Vernieuw de toestemming om transacties zonder onderbreking te blijven synchroniseren.',
                action: 'Nu vernieuwen',
            };
        case ConnectionStatus.Expired:
            return {
                eyebrow: connectionLabel,
                title: 'Koppeling verlopen',
                detail: 'Nieuwe transacties zijn gepauzeerd. Koppel opnieuw om weer te synchroniseren.',
                action: 'Opnieuw koppelen',
            };
        case ConnectionStatus.Manual:
            return {
                eyebrow: 'Spaarrekening',
                title: 'Handmatig toegevoegd',
                detail: 'Het saldo wordt niet automatisch bijgewerkt.',
                action: '',
            };
        default: {
            const _exhaustive: never = status;
            return _exhaustive;
        }
    }
}

export function captionFor(status: ConnectionStatus, lastSynced: string): string {
    switch (status) {
        case ConnectionStatus.Connected:
            return lastSynced ? `Verbonden · gesynchroniseerd ${lastSynced}` : 'Verbonden';
        case ConnectionStatus.Expiring:
            return 'Koppeling verloopt binnenkort';
        case ConnectionStatus.Expired:
            return 'Koppeling verlopen · transacties gepauzeerd';
        case ConnectionStatus.Manual:
            return 'Handmatig toegevoegd';
        default: {
            const _exhaustive: never = status;
            return _exhaustive;
        }
    }
}

function StatusDot({status}: {status: ConnectionStatus}) {
    return <span className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLASS[status]}`} />;
}

const INDICATOR_CLASS: Record<ConnectionStatus, string> = {
    [ConnectionStatus.Connected]: 'bg-emerald-500 ring-[3px] ring-emerald-500/20',
    [ConnectionStatus.Expiring]: 'bg-amber-500 ring-[3px] ring-amber-500/20',
    [ConnectionStatus.Expired]: 'bg-red-500 ring-[3px] ring-red-500/20',
    [ConnectionStatus.Manual]: '',
};

export function StatusIndicator({status}: {status: ConnectionStatus}) {
    if (status === ConnectionStatus.Manual) {
        return null;
    }
    return <span aria-hidden className={`h-1.5 w-1.5 shrink-0 rounded-full ${INDICATOR_CLASS[status]}`} />;
}

const PINNED_PANEL_CLASS = 'text-gray-800';

const HOVER_PANEL_CLASS = 'pointer-events-none text-white bg-[linear-gradient(180deg,rgba(8,10,14,0.30)_0%,rgba(8,10,14,0.72)_58%,rgba(8,10,14,0.9)_100%)] opacity-0 backdrop-blur-[2px] group-focus-visible:pointer-events-auto group-focus-visible:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-has-[:focus-visible]:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100';

interface Props {
    status: ConnectionStatus;
    holderName: string;
    accountLine: string;
    lastSynced: string;
    connectionLabel: string;
    onAction?: () => void;
}

export function ConnectionPanel({status, holderName, accountLine, lastSynced, connectionLabel, onAction}: Props) {
    const copy = statusCopy(status, lastSynced, connectionLabel);
    const isPinned = keepsPanelOpen(status);
    const showAction = copy.action !== '' && onAction !== undefined;

    return (
        <div className={`absolute inset-0 z-30 flex flex-col justify-end p-[7.5%] transition-opacity duration-[260ms] ease-out motion-reduce:transition-none ${isPinned ? PINNED_PANEL_CLASS : HOVER_PANEL_CLASS}`}>
            {!isPinned && (
                <div className="flex items-center gap-[7px] text-[10px] uppercase tracking-[0.08em] opacity-80">
                    <StatusDot status={status} />
                    {copy.eyebrow}
                </div>
            )}
            <div className="mt-1 text-[22px] font-bold leading-[1.04] tracking-[-0.01em]">{copy.title}</div>
            <div className="mt-1.5 truncate text-[10px] font-semibold uppercase tracking-[0.08em] opacity-80" title={holderName}>
                {holderName}
            </div>
            {accountLine !== '' && (
                <div className="font-mono text-[12px] tracking-[0.06em] opacity-90">{accountLine}</div>
            )}
            {!isPinned && (
                <div className="mt-1 max-w-[82%] text-[11px] leading-[1.35] opacity-85">{copy.detail}</div>
            )}
            {isPinned && lastSynced !== '' && (
                <div className="mt-1 text-[11px] leading-[1.35] opacity-85">Laatst gesynchroniseerd {lastSynced}</div>
            )}
            {showAction && (
                <button
                    type="button"
                    onClick={onAction}
                    className={`mt-3 inline-flex items-center gap-1.5 self-start rounded-lg px-3.5 py-2 text-xs font-semibold transition hover:-translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${ACTION_CLASS[status]}`}
                >
                    {status === ConnectionStatus.Expired && <ArrowPathIcon className="h-3.5 w-3.5" />}
                    {copy.action}
                </button>
            )}
        </div>
    );
}
